import React from 'react';

/**
 * 🎨 COMPONENTE BUTTON - Reutilizável
 * 
 * Botão flexível e reutilizável com múltiplas variantes e tamanhos.
 * Usa TailwindCSS para estilização.
 * 
 * @param {string} variant - Variante do botão: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost'
 * @param {string} size - Tamanho: 'sm' | 'md' | 'lg'
 * @param {boolean} disabled - Se o botão está desabilitado
 * @param {boolean} loading - Se está em estado de carregamento
 * @param {function} onClick - Função chamada ao clicar
 * @param {string} type - Tipo do botão: 'button' | 'submit' | 'reset'
 * @param {string} className - Classes CSS adicionais
 * @param {ReactNode} children - Conteúdo do botão
 * @param {object} props - Outras props do HTML button
 */
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  ...props 
}) => {
  // Base classes - aplicadas a todos os botões
  const baseClasses = `
    font-medium 
    rounded-lg 
    transition-all 
    duration-200 
    focus:outline-none 
    focus:ring-2 
    focus:ring-offset-2 
    disabled:opacity-50 
    disabled:cursor-not-allowed 
    disabled:hover:transform-none
    inline-flex 
    items-center 
    justify-center
    gap-2
  `;
  
  // Variantes de cor e estilo
  const variants = {
    primary: `
      bg-primary 
      text-white 
      hover:bg-primary-dark 
      focus:ring-primary 
      shadow-md 
      hover:shadow-lg
      hover:-translate-y-0.5
    `,
    secondary: `
      bg-gray-200 
      text-gray-800 
      hover:bg-gray-300 
      focus:ring-gray-500
      shadow-sm
    `,
    danger: `
      bg-red-600 
      text-white 
      hover:bg-red-700 
      focus:ring-red-500 
      shadow-md 
      hover:shadow-lg
    `,
    outline: `
      border-2 
      border-primary 
      text-primary 
      bg-transparent 
      hover:bg-primary 
      hover:text-white 
      focus:ring-primary
    `,
    ghost: `
      bg-transparent 
      text-gray-700 
      hover:bg-gray-100 
      focus:ring-gray-300
      shadow-none
    `,
  };
  
  // Tamanhos
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };
  
  // Classes finais combinadas
  const buttonClasses = `
    ${baseClasses}
    ${variants[variant]}
    ${sizes[size]}
    ${className}
  `.trim().replace(/\s+/g, ' ');
  
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={buttonClasses}
      {...props}
    >
      {loading ? (
        <>
          {/* Spinner de loading */}
          <svg 
            className="animate-spin h-4 w-4" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Carregando...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;

